/**
 * The last few errors this tab hit (uncaught errors, rejected promises and
 * crashes the error boundary caught), so a bug report can carry them. Kept
 * in sessionStorage, since people often reload before reporting. Keys,
 * tokens and passwords in a message are blanked first. Fed by logError in
 * log.ts; read by Bug Report. Tested by tests/bugreport.test.mjs.
 */

export const RECENT_ERRORS_KEY = 'recent-errors';
export const MAX_ERRORS = 5;
/** Older than this, an error probably isn't the one being reported. */
export const ERROR_MAX_AGE_MS = 30 * 60_000;
/** Each line's length, as sent. */
export const ERROR_LINE_MAX = 300;

export interface RecentError {
  /** When, in ms. */
  at: number;
  /** logError's ID, which finds the full event in the site log. */
  id: string;
  /** The path it happened on. */
  page: string;
  /** "TypeError: x is undefined". */
  message: string;
}

/**
 * Blanks the values of anything named like a key, token, password or
 * signature (key=, access_token=, t=, code=…) in text such as a URL.
 */
export function scrubSecrets(text: string): string {
  return text.replace(/(^|[?&#;\s"'(])([\w.-]*(?:key|token|secret|password|passwd|pass|pw|sig|signature|auth)|t|code)=[^&\s#"'<>)]+/gi, '$1$2=…');
}

/** One error as a line for the report: its ID, page and message. */
export function errorLine(e: RecentError): string {
  return `${e.id} · ${e.page} · ${e.message}`.replace(/\s+/g, ' ').trim().slice(0, ERROR_LINE_MAX);
}

/** The list with `entry` added: newest last, at most MAX_ERRORS, none older than ERROR_MAX_AGE_MS. */
export function withError(list: readonly RecentError[], entry: RecentError): RecentError[] {
  const clean: RecentError = {
    ...entry,
    page: scrubSecrets(entry.page).slice(0, 200),
    message: scrubSecrets(entry.message).slice(0, ERROR_LINE_MAX),
  };
  return [...fresh(list, entry.at), clean].slice(-MAX_ERRORS);
}

/** The errors still recent at `now`. */
export function fresh(list: readonly RecentError[], now: number): RecentError[] {
  return list.filter(e => now - e.at <= ERROR_MAX_AGE_MS && e.at <= now + 60_000);
}

/** A stored list, checked field by field; [] if it isn't one. */
export function parseErrors(raw: string | null): RecentError[] {
  if (!raw) return [];
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    return data.filter((e): e is RecentError => Boolean(e) && typeof e === 'object'
      && Number.isFinite((e as RecentError).at)
      && typeof (e as RecentError).id === 'string'
      && typeof (e as RecentError).page === 'string'
      && typeof (e as RecentError).message === 'string').slice(-MAX_ERRORS);
  } catch {
    return [];
  }
}

// In memory too, for when storage is blocked.
let memory: RecentError[] = [];

function load(): RecentError[] {
  try {
    return parseErrors(sessionStorage.getItem(RECENT_ERRORS_KEY));
  } catch {
    return memory;
  }
}

/** Remembers an error for a bug report. Never throws. */
export function recordError(entry: RecentError): void {
  try {
    memory = withError(load(), entry);
    sessionStorage.setItem(RECENT_ERRORS_KEY, JSON.stringify(memory));
  } catch {
    /* storage unavailable: kept in memory for this page */
  }
}

/** This tab's recent errors, oldest first. */
export function recentErrors(now = Date.now()): RecentError[] {
  return fresh(load(), now);
}

/** Forgets them, once a report has taken them along. */
export function clearRecentErrors(): void {
  memory = [];
  try {
    sessionStorage.removeItem(RECENT_ERRORS_KEY);
  } catch {
    /* storage unavailable */
  }
}
